/**
 * NO.SO Coffee Packaging Designer — prompt assembly
 * Source of truth for both Cursor Agent (skill) and Admin Cover Prompt button.
 * Abstract geometry only. No literal nature, text, logo, or mockup.
 */

import palettes from './palettes.json'
import flavors from './flavors.json'
import processMap from './process.json'
import composition from './composition.json'
import emotions from './emotions.json'

export interface PackagingInput {
	country: string
	process: string | string[]
	flavors: string[]
	acidity?: string
	body?: string
	sweetness?: string
	altitude?: number | string
	roast?: string
	kind?: 'single' | 'blend'
	extra?: string
}

export interface BuiltPrompt {
	mapping: {
		origin: string
		palette: string[]
		processTexture: string[]
		flavorGeometry: string[]
		acidityMovement: string
		bodyForm: string
		sweetnessPattern: string
		altitudeBalance: string
		roastDensity: string
	}
	prompt: string
	negative: string
}

type AnyMap = Record<string, any>

function arr(v: string | string[] | undefined): string[] {
	if (!v) return []
	return (Array.isArray(v) ? v : [s(v)]).map(s).filter(Boolean)
}

function s(v: unknown): string {
	return String(v ?? '').trim()
}

function matchKey(raw: string, table: AnyMap, nestedAliases = true): string | null {
	const t = s(raw)
	if (!t) return null
	if (table[t]) return t
	for (const [key, val] of Object.entries(table)) {
		if (key.toLowerCase() === t.toLowerCase()) return key
		const aliases: string[] = nestedAliases ? val?.aliases || [] : []
		if (aliases.some((a) => a.toLowerCase() === t.toLowerCase())) return key
	}
	return null
}

function resolveOrigin(country: string, paletteData: AnyMap): { name: string; swatches: string[]; hex: string[] } {
	const origins = paletteData.origins || {}
	const key = matchKey(country, origins)
	if (key) {
		const o = origins[key]
		return { name: key, swatches: o.swatches, hex: o.hex }
	}
	const d = paletteData.default
	return { name: country || 'Studio Neutral', swatches: d.swatches, hex: d.hex }
}

function resolveProcess(process: string | string[], processJson: AnyMap): { name: string; texture: string[] } {
	const primary = arr(process)[0] || 'Washed'
	const table = processJson.processes || {}
	const key = matchKey(primary, table) || 'Washed'
	return { name: key, texture: table[key]?.texture || ['Grid', 'Fine Lines'] }
}

function resolveFlavors(flavorList: string[], flavorsJson: AnyMap): string[] {
	const table = flavorsJson.flavors || {}
	const out: string[] = []
	for (const f of flavorList) {
		const key = matchKey(f, table)
		const geo: string[] = key ? table[key].geometry || [] : []
		out.push(...geo)
	}
	const unique = [...new Set(out)].slice(0, 4)
	if (!unique.length) return flavorsJson.fallbackGeometry || ['Balanced Abstract Grid']
	return unique
}

function resolveAxis(value: string | undefined, table: AnyMap, field: string, fallbackKey: string): string {
	const key = matchKey(value || fallbackKey, table) || fallbackKey
	return table[key]?.[field] || table[fallbackKey]?.[field] || ''
}

function resolveAltitude(altitude: number | string | undefined, altTable: AnyMap, fallback: string): string {
	if (altitude == null || altitude === '') {
		return altTable[fallback]?.balance || 'Vertical Balance'
	}
	if (typeof altitude === 'string' && altTable[altitude]) {
		return altTable[altitude].balance
	}
	const n = typeof altitude === 'number' ? altitude : parseInt(String(altitude).replace(/[^\d]/g, ''), 10)
	if (!Number.isFinite(n)) return altTable[fallback]?.balance || 'Vertical Balance'
	if (n < 1200) return altTable['Below 1200'].balance
	if (n <= 1600) return altTable['1200-1600'].balance
	if (n <= 2000) return altTable['1600-2000'].balance
	return altTable['Above 2000'].balance
}

/** Low-level builder (agent may pass custom data maps for experiments) */
export function buildPrompt(
	input: PackagingInput,
	data: {
		palettes: AnyMap
		flavors: AnyMap
		process: AnyMap
		composition: AnyMap
		emotions: AnyMap
	}
): BuiltPrompt {
	const defaults = data.emotions.defaults || {}
	const origin = resolveOrigin(input.country, data.palettes)
	const proc = resolveProcess(input.process, data.process)
	const flavorGeometry = resolveFlavors(input.flavors || [], data.flavors)

	const acidityMovement = resolveAxis(input.acidity, data.emotions.acidity, 'movement', defaults.acidity || 'Medium')
	const bodyForm = resolveAxis(input.body, data.emotions.body, 'form', defaults.body || 'Silky')
	const sweetnessPattern = resolveAxis(
		input.sweetness,
		data.emotions.sweetness,
		'pattern',
		defaults.sweetness || 'Medium'
	)
	const altitudeBalance = resolveAltitude(input.altitude, data.emotions.altitude, defaults.altitude || '1600-2000')
	const roastDensity = resolveAxis(input.roast, data.emotions.roast, 'density', defaults.roast || 'Light')

	const paletteWords = origin.swatches.join(', ')
	const hexWords = (origin.hex || []).join(', ')
	const textureWords = proc.texture.join(', ')
	const geometryWords = flavorGeometry.join(', ')
	const quality = (data.composition.qualityTags || []).join(', ')
	const negatives = (data.composition.negativePrompt || []).join(', ')

	const prompt = [
		'premium specialty coffee packaging illustration',
		'Swiss graphic design, editorial layout, contemporary museum poster',
		'abstract geometry, vector style, soft paper texture',
		'quiet luxury, calm, intelligent, minimal, timeless',
		'vertical composition, one dominant visual object',
		'strong visual hierarchy, large negative space, balanced asymmetry',
		`color palette: ${paletteWords}${hexWords ? ` (${hexWords})` : ''}`,
		`process texture language: ${textureWords}`,
		`flavor as geometry: ${geometryWords}`,
		`acidity movement: ${acidityMovement}`,
		`body form: ${bodyForm}`,
		`sweetness pattern: ${sweetnessPattern}`,
		`altitude balance: ${altitudeBalance}`,
		`roast density: ${roastDensity}`,
		'white background allowed, packaging-ready artwork only',
		'no text, no logo, no typography, no mockup',
		quality,
		input.extra || ''
	]
		.filter(Boolean)
		.join(', ')

	return {
		mapping: {
			origin: origin.name,
			palette: origin.swatches,
			processTexture: proc.texture,
			flavorGeometry,
			acidityMovement,
			bodyForm,
			sweetnessPattern,
			altitudeBalance,
			roastDensity
		},
		prompt,
		negative: negatives
	}
}

/** App + skill default: always uses JSON maps in this skill folder */
export function buildCoverPrompt(input: PackagingInput): BuiltPrompt {
	return buildPrompt(input, {
		palettes,
		flavors,
		process: processMap,
		composition,
		emotions
	})
}

export const skillData = { palettes, flavors, process: processMap, composition, emotions }
